import React, { useState, useEffect } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, Package, ShieldAlert, Inbox, DollarSign, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const CATS = ['AV Equipment', 'Stationery', 'Gifts & Swag', 'Decor', 'Transport', 'Printing', 'Clothing', 'Misc'];
const STATUS = ['needed', 'ordered', 'received', 'in-use', 'returned'];

function Modal({ item, onSave, onClose }) {
  const [form, setForm] = useState(item || {
    name: '', category: 'Misc', quantity: 1, unit: 'pcs',
    status: 'needed', vendor: '', unitPrice: '',
    notes: '', assignedTo: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Item name is required.'); return; }
    if (form.quantity && Number(form.quantity) < 0) { setError('Quantity cannot be negative.'); return; }
    if (form.unitPrice && Number(form.unitPrice) < 0) { setError('Unit price cannot be negative.'); return; }
    setSaving(true);
    setError('');
    try { await onSave(form); } catch { setError('Failed to save. Please try again.'); setSaving(false); }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{item ? 'Edit Supply Details' : 'Add Strategic Supply Item'}</h3>
            <p className="text-xs text-slate-500 mt-1">Configure asset quantity unit, price quotes, and custody owners</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Item / Asset Name *</label>
            <input 
              value={form.name} 
              onChange={e => set('name', e.target.value)} 
              placeholder="e.g. HDMI Splitter Extender" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</label>
              <select 
                value={form.category} 
                onChange={e => set('category', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Supply Status</label>
              <select 
                value={form.status} 
                onChange={e => set('status', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Quantity</label>
              <input 
                type="number" 
                value={form.quantity} 
                onChange={e => set('quantity', e.target.value)} 
                min={1} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Unit type</label>
              <input 
                value={form.unit} 
                onChange={e => set('unit', e.target.value)} 
                placeholder="pcs / cases" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display">Unit Price (INR)</label>
              <input 
                type="number" 
                value={form.unitPrice} 
                onChange={e => set('unitPrice', e.target.value)} 
                placeholder="750" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white font-semibold outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display font-normal text-slate-600">Supplier / Vendor</label>
              <input 
                value={form.vendor} 
                onChange={e => set('vendor', e.target.value)} 
                placeholder="e.g. Amazon Local Hub" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Assigned Offsite Custodian</label>
            <input 
              value={form.assignedTo} 
              onChange={e => set('assignedTo', e.target.value)} 
              placeholder="e.g. Ravi Kumar" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Internal Remarks</label>
            <textarea 
              value={form.notes} 
              onChange={e => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Storage location codes, courier tracker identifiers..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {item ? 'Commit Supply' : 'Deploy Supply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusClass = { 
  needed: 'sh-badge-danger', 
  ordered: 'sh-badge-warning', 
  received: 'sh-badge-info', 
  'in-use': 'sh-badge-success', 
  returned: 'sh-badge-outline' 
};

export default function Inventory() {
  const { data: items, loading } = useStore('inventory');
  const [modal, setModal] = useState(null);
  const [catFilter, setCatFilter] = useState('all');

  const save = async (form) => {
    await put('inventory', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Supply item updated.' : 'Supply item added.');
  };

  const del = async (id) => {
    const ok = await confirm('Remove this supply item from the inventory?', 'Delete Item');
    if (ok) {
      try {
        await remove('inventory', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Item deleted.');
      } catch {
        toast.error('Failed to delete item.');
      }
    }
  };

  const filtered = catFilter === 'all' ? items : items.filter(i => i.category === catFilter);
  const totalValue = items.reduce((s, i) => s + ((parseFloat(i.unitPrice) || 0) * (parseInt(i.quantity) || 0)), 0);
  const needed = items.filter(i => i.status === 'needed').length;

  if (loading) return <PageLoader label="Loading inventory…" sublabel="Fetching supplies and assets" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Package style={{ color: 'var(--accent)' }} size={24} />
            <span>Offsite Inventory & Assets</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Track collaborative tech gear, printing materials, welcome hampers, clothing sizes, and direct custodians
          </p>
        </div>
        <button 
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm" 
          onClick={() => setModal('new')}
        >
          <Plus size={16} />
          <span>Add Asset Log</span>
        </button>
      </div>

      {/* Summary Matrix Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Assets Registered</span>
          </div>
          <div className="sh-stat-value text-slate-900">{items.length} properties</div>
          <span className="text-xs text-slate-500">Tracked day-wise items</span>
        </div>

        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label font-display">Accumulated Asset Value</span>
            <DollarSign className="text-emerald-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900">₹{totalValue.toLocaleString('en-IN')}</div>
          <span className="text-xs text-slate-500 block">Total physical capital reserves online</span>
        </div>

        <div className="sh-stat-card border-l-4 border-red-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Procurements Outstanding</span>
            <ShieldAlert className="text-red-500" size={14} />
          </div>
          <div className={`sh-stat-value ${needed > 0 ? 'text-red-655 font-bold text-red-650' : 'text-slate-500'}`}>{needed} gaps</div>
          <span className="text-xs text-slate-500 block">Need urgent purchasing approval</span>
        </div>
      </div>

      {/* category selectors */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 select-none">
          <button 
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition shrink-0 ${catFilter === 'all' ? 'bg-slate-900 text-white border-slate-900 animate-none' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} 
            onClick={() => setCatFilter('all')}
          >
            All Inventory ({items.length})
          </button>
          {CATS.map((c) => (
            <button 
              key={c} 
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition shrink-0 capitalize ${catFilter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} 
              onClick={() => setCatFilter(c)}
            >
              {c} ({items.filter(item => item.category === c).length})
            </button>
          ))}
        </div>
      </div>

      {/* Main Table or Empty */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">Inventory lists clear</h3>
          <p className="text-sm text-slate-500 mt-1">Refine active department categories or log a new material item directly</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-sm">
          <div className="table-wrap">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Asset / Item Info</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Sector</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Quantity</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Unit Cost</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Accumulated Cost</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left w-24">Custody Status</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Internal Custodian</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(item => {
                  const accum = (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0);
                  return (
                    <tr key={item.id} className="hover:bg-slate-50/20 transition">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{item.name}</div>
                        {item.vendor && <div className="text-xs text-slate-500">{item.vendor}</div>}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs font-semibold capitalize font-display">
                        {item.category}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-slate-800">
                        {item.quantity} {item.unit || 'pcs'}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-600">
                        {item.unitPrice ? `₹${Number(item.unitPrice).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold tracking-tight text-slate-900">
                        {accum > 0 ? `₹${accum.toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`sh-badge ${statusClass[item.status] || 'sh-badge-outline'}`}>{item.status}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-700 font-bold">{item.assignedTo || 'General pool'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center gap-1.5 justify-end flex-wrap">
                          <button 
                            className="p-1.5 text-slate-450 text-slate-500 hover:text-slate-900 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-md transition" 
                            onClick={() => setModal(item)}
                          >
                            <Edit2 size={13} />
                          </button>
                          <button 
                            className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-md transition" 
                            onClick={() => del(item.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <Modal item={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}
